import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Form, FormSpy } from "react-final-form";

import { makeApiCall } from "@lib/api/helpers";
import {
  getCategoriesVehicleApi,
  getSubCategoriesVehicleApi,
  getBrandsVehicleApi,
  getFuelTypesVehicleApi,
  checkVinVehicleApi,
  getActivityTypesRCAApi,
} from "@lib/api";
import { addAlert, addModal } from "@store/app/actions";
import Button from "@components/Button";
import VehicleStateSection from "@containers/RCA/RCAVehicul/VehicleStateSection";
import VehicleDataSection from "@containers/RCA/RCAVehicul/VehicleDataSection";
import Heading from "@components/Heading";
import CivExampleModal from "@containers/RCA/RCAVehicul/CivExampleModal";

const RCAVehicul = ({
  apiData,
  onFormDataUpdate,
  onApiDataUpdate,
  onStepChange,
  onLoadingStateChange,
  formData,
  addAlert,
  onErrorsUpdate,
  addModal,
}) => {
  const initialValues = useMemo(
    () => ({
      vehicle: {
        ...formData.vehicle,
        vehicleRegistrationStatus:
          formData.vehicle?.vehicleRegistrationStatus || "1",
      },
    }),
    []
  );

  useEffect(() => {
    const getInitialData = async () => {
      onLoadingStateChange(true);

      const promises = [];

      if (!apiData.vehicleCategories) {
        promises.push(fetchVehicleCategories());
      }

      if (!apiData.vehicleBrands) {
        promises.push(fetchVehicleBrands());
      }

      if (!apiData.vehicleFuelTypes) {
        promises.push(fetchFuelTypes());
      }

      if (!apiData.vehicleActivityTypes) {
        promises.push(fetchVehicleActivityTypes());
      }

      await Promise.all(promises);

      onLoadingStateChange(false);
    };

    if (
      !(
        apiData.vehicleCategories ||
        apiData.vehicleBrands ||
        apiData.vehicleFuelTypes ||
        apiData.vehicleActivityTypes
      )
    ) {
      getInitialData();
    }
  }, []);

  const fetchVehicleSubcategories = async ({ categoryId }) => {
    const response = await makeApiCall({
      apiCall: getSubCategoriesVehicleApi,
      apiArgs: [categoryId],
      formatResponse: (response) => response.data.data,
      preFail: () => {
        addAlert({
          message: "Nu am putut incarca subcategoriile de vehicule.",
          variant: "error",
        });

        onErrorsUpdate({
          message: "Nu am putut incarca subcategoriile de vehicule.",
          variant: "error",
        });
      },
    });

    if (response?.items?.length) {
      onApiDataUpdate({
        vehicleSubcategories: {
          ...apiData.vehicleSubcategories,
          [categoryId]: response.items,
        },
      });
    }
  };

  const fetchVehicleCategories = async () => {
    const response = await makeApiCall({
      apiCall: getCategoriesVehicleApi,
      formatResponse: (response) => response.data.data,
      preFail: () => {
        addAlert({
          message: "Nu am putut incarca categoriile de vehicule.",
          variant: "error",
        });

        onErrorsUpdate({
          message: "Nu am putut incarca categoriile de vehicule.",
          variant: "error",
        });
      },
    });

    if (response?.items?.length) {
      onApiDataUpdate({ vehicleCategories: response.items });
    }
  };

  const fetchVehicleBrands = async () => {
    const response = await makeApiCall({
      apiCall: getBrandsVehicleApi,
      formatResponse: (response) =>
        response.data.data.items.sort((a, b) =>
          a.name.trim().localeCompare(b.name.trim())
        ),
      preFail: () => {
        addAlert({
          message: "Nu am putut incarca marcile de vehicule.",
          variant: "error",
        });

        onErrorsUpdate({
          message: "Nu am putut incarca marcile de vehicule.",
          variant: "error",
        });
      },
    });

    if (response?.length) {
      onApiDataUpdate({ vehicleBrands: response });
    }
  };

  const fetchFuelTypes = async () => {
    const response = await makeApiCall({
      apiCall: getFuelTypesVehicleApi,
      formatResponse: (response) => response.data.data,
      preFail: () => {
        addAlert({
          message: "Nu am putut incarca tipurile de combustibil.",
          variant: "error",
        });

        onErrorsUpdate({
          message: "Nu am putut incarca tipurile de combustibil.",
          variant: "error",
        });
      },
    });

    if (response?.items?.length) {
      onApiDataUpdate({ vehicleFuelTypes: response.items });
    }
  };

  const fetchVehicleActivityTypes = async () => {
    const response = await makeApiCall({
      apiCall: getActivityTypesRCAApi,
      formatResponse: (response) => response.data.data,
      preFail: () => {
        addAlert({
          message:
            "Nu am putut incarca tipurile de activitati ale vehiculului.",
          variant: "error",
        });

        onErrorsUpdate({
          message:
            "Nu am putut incarca tipurile de activitati ale vehiculului.",
          variant: "error",
        });
      },
    });

    if (response?.items?.length) {
      onApiDataUpdate({ vehicleActivityTypes: response.items });
    }
  };

  const handleVinCheck = async ({ vin, form }) => {
    if (vin.length < 17) {
      return;
    }

    let newVinData = {};

    if (
      apiData.vinData &&
      apiData.vinData.vin.trim().toLowerCase() === vin.trim().toLowerCase()
    ) {
      newVinData = apiData.vinData;
    } else {
      onLoadingStateChange(true);

      const response = await makeApiCall({
        apiCall: checkVinVehicleApi,
        apiArgs: [vin],
        formatResponse: (response) => response.data.data,
        preFail: () => {
          addAlert({
            message:
              "Nu am putut verifica VIN-ul. Va rugam verificati si incercati din nou.",
            variant: "error",
          });
        },
      });

      if (response) {
        onApiDataUpdate({ vinData: response });
        newVinData = response;
      }

      onLoadingStateChange(false);
    }

    // Update form values with VIN data
    if (
      newVinData.vehicleCategoryId &&
      !apiData.vehicleSubcategories?.[newVinData.vehicleCategoryId]
    ) {
      await getFormData({
        type: "vehicleSubcategoriesData",
        payload: { categoryId: newVinData.vehicleCategoryId },
      });
    }

    if (newVinData.makeId) {
      const vehicleBrand = apiData.vehicleBrands.find(
        (brand) => brand.id === newVinData.makeId
      );

      if (vehicleBrand) {
        form.change("vehicle.makeId", vehicleBrand.id);
      }
    }

    if (newVinData) {
      form.change("vehicle.vehicleCategoryId", newVinData.vehicleCategoryId);
      form.change("vehicle.commercialName", newVinData.commercialName);
      form.change(
        "vehicle.vehicleSubCategoryId",
        newVinData.vehicleSubCategoryId
      );
      form.change("vehicle.productionYear", newVinData.productionYear);
      form.change("vehicle.fuelTypeId", newVinData.fuelId);
      form.change("vehicle.engineCapacity", newVinData.engineCapacity);
      form.change("vehicle.enginePowerKw", newVinData.enginePowerKw);
      form.change("vehicle.maxWeight", newVinData.maxWeight);
      form.change("vehicle.seatsNumber", newVinData.seatsNumber);
    }
  };

  const getFormData = async ({ type, payload }) => {
    onLoadingStateChange(true);

    switch (type) {
      case "vehicleSubcategoriesData":
        await fetchVehicleSubcategories(payload);
        break;

      case "vinCheckData":
        await handleVinCheck(payload);
        break;

      default:
        break;
    }

    onLoadingStateChange(false);
  };

  const handleFormDataChange = ({ values }) => {
    // Save form data for retention, but only if changed
    if (JSON.stringify(values.vehicle) !== JSON.stringify(formData.vehicle)) {
      onFormDataUpdate({ vehicle: { ...formData.vehicle, ...values.vehicle } });
    }

    if (
      values.vehicle.vehicleCategoryId &&
      !apiData.vehicleSubcategories?.[values.vehicle.vehicleCategoryId]
    ) {
      getFormData({
        type: "vehicleSubcategoriesData",
        payload: { categoryId: values.vehicle.vehicleCategoryId },
      });
    }
  };

  const onSubmit = (values) => {
    const {
      fullDetailsFetched,
      vehicleCategoryId,
      vehicleSubCategoryId,
      vehicleRegistrationStatus,
      makeId,
      fuelTypeId,
      productionYear,
      engineCapacity,
      maxWeight,
      enginePowerKw,
      seatsNumber,
      graweKilometraj,
      activityTypeId,
      ...vehicleData
    } = values.vehicle;

    const payload = {
      vehicle: {
        ...vehicleData,
        vehicleCategoryId: Number(vehicleCategoryId),
        vehicleSubCategoryId: Number(vehicleSubCategoryId),
        vehicleRegistrationStatus: Number(vehicleRegistrationStatus),
        makeId: Number(makeId),
        productionYear: Number(productionYear),
        fuelTypeId: Number(fuelTypeId),
        engineCapacity: Number(engineCapacity),
        maxWeight: Number(maxWeight),
        enginePowerKw: Number(enginePowerKw),
        seatsNumber: Number(seatsNumber),
        graweKilometraj: Number(graweKilometraj),
        activityTypeId: Number(activityTypeId),
        itpExpiration: formData.vehicle.itpExpiration,
      },
    };

    onFormDataUpdate(payload);
    onStepChange(4);
  };

  return (
    <>
      <Form
        key="RCAVehiculForm"
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, values, form }) => (
          <form onSubmit={handleSubmit}>
            <div className="space-y-[20px]">
              <Heading
                as="h4"
                title="Date despre autovehicul"
                display="h2"
                sameSizeOnMobile
              />

              <FormSpy
                subscription={{ values: true }}
                onChange={({ values }) => {
                  handleFormDataChange({ values });
                }}
              />

              <VehicleStateSection values={values} />

              <div className="bg-white p-[16px] text-center">
                <Button
                  type="button"
                  onClick={() => addModal("CivExampleModal")}
                  title="Legenda talon"
                  isCatalog
                  variant="third"
                />
              </div>

              <div className="bg-white p-[16px]">
                <VehicleDataSection
                  form={form}
                  apiData={apiData}
                  getFormData={getFormData}
                  onFormDataUpdate={onFormDataUpdate}
                  formData={formData}
                />
              </div>
            </div>

            <div className="pt-[20px] mt-[20px] text-center -mx-[15px] lg:-mx-[20px] -mb-[20px] bg-white">
              <Button
                variant="secondary"
                type="submit"
                title="Pasul urmator"
                classesProp="min-w-[300px] text-[20px] font-medium"
                hasMinWidth
                disabled={
                  form.getState().invalid || !formData.vehicle.itpExpiration
                }
              />
            </div>
          </form>
        )}
      />

      <CivExampleModal />
    </>
  );
};

export default connect(null, { addAlert, addModal })(RCAVehicul);
